import React, { useEffect, useMemo, useState } from 'react'
import { CButton, CForm, CFormLabel, CFormSelect, CSpinner } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../shared/icons'
import { useUI } from '../../shared/ui/UIProvider'
import { request } from '../../services'

function userUsername(user) {
  return String(user?.username || user?.user || user?.usuario || user?.id || '').trim()
}

function userPcc(user) {
  return String(user?.pcc || user?.PCC || user?.pcc_code || '').toUpperCase()
}

function userParentPcc(user, fallback = '') {
  return String(user?.parent_pcc || user?.parentPcc || user?.parentPCC || fallback || '').toUpperCase()
}

function toBool(value) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

function sameUsername(left, right) {
  const leftValue = String(left ?? '').trim()
  const rightValue = String(right ?? '').trim()
  if (leftValue === rightValue) return true
  const leftNumber = Number(leftValue)
  const rightNumber = Number(rightValue)
  return Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && leftNumber === rightNumber
}

function normalizePermission(row = {}, fallbackParentPcc = '', fallbackPcc = '') {
  return {
    ...row,
    username: String(row.username ?? row.id ?? '').trim(),
    parentPcc: String(row.parentPcc || row.parent_pcc || fallbackParentPcc || '').toUpperCase(),
    pcc: String(row.pcc || row.PCC || fallbackPcc || '').toUpperCase(),
    enableScriptGenerator: toBool(row.enableScriptGenerator ?? row.enable_script_generator),
    enableRepositories: toBool(row.enableRepositories ?? row.enable_repositories),
  }
}

function emptyForm(parentPcc, pcc) {
  return {
    username: '',
    parentPcc: String(parentPcc || '').toUpperCase(),
    pcc: String(pcc || '').toUpperCase(),
    enableScriptGenerator: false,
    enableRepositories: false,
  }
}

function DashboardPermissionsPanel({ parentPcc, pccs = [], users = [] }) {
  const { toast, confirm } = useUI()
  const [dashboardPcc, setDashboardPcc] = useState('')
  const [dashboardPermissions, setDashboardPermissions] = useState([])
  const [dashboardPermissionForm, setDashboardPermissionForm] = useState(emptyForm(parentPcc, ''))
  const [dashboardSelectedUser, setDashboardSelectedUser] = useState('')
  const [editingKey, setEditingKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState('')

  useEffect(() => {
    if (pccs.length === 0) return
    if (!dashboardPcc || !pccs.some((item) => item.pcc === dashboardPcc)) {
      setDashboardPcc(pccs[0].pcc)
    }
  }, [pccs, dashboardPcc])

  const availableUsers = useMemo(() => {
    return users
      .filter((user) => userPcc(user) === dashboardPcc)
      .map((user) => ({
        id: userUsername(user),
        username: userUsername(user),
        name: user.name || '',
        email: user.email || '',
        parentPcc: userParentPcc(user, parentPcc),
        pcc: userPcc(user),
      }))
      .filter((user) => user.id)
  }, [users, dashboardPcc, parentPcc])

  useEffect(() => {
    if (!parentPcc || !dashboardPcc) return
    loadDashboardPermissions()
  }, [parentPcc, dashboardPcc])

  async function loadDashboardPermissions() {
    if (!parentPcc || !dashboardPcc) return
    setLoading(true)
    const res = await request('dashboard_permissions_list', {
      params: { parentPcc, pcc: dashboardPcc },
    })
    setLoading(false)
    if (!res.ok) {
      const detail = res.data?.message || res.data?.error || 'No se pudieron cargar los permisos dashboard'
      toast(detail, 'danger')
      setDashboardPermissions([])
      setDashboardPermissionForm(emptyForm(parentPcc, dashboardPcc))
      setDashboardSelectedUser('')
      return
    }
    const responseParentPcc = res.data?.parentPcc || parentPcc
    const responsePcc = res.data?.pcc || dashboardPcc
    const permissions = Array.isArray(res.data?.permissions)
      ? res.data.permissions.map((row) => normalizePermission(row, responseParentPcc, responsePcc))
      : []
    setDashboardPermissions(permissions)
    setDashboardPermissionForm(emptyForm(res.data?.parentPcc || parentPcc, res.data?.pcc || dashboardPcc))
    setDashboardSelectedUser('')
    setEditingKey('')
  }

  function selectUser(username) {
    const normalizedUsername = String(username || '')
    const selected = availableUsers.find((user) => sameUsername(user.id, normalizedUsername))
    const configured = dashboardPermissions.find((user) => (
      sameUsername(user.username || user.id, normalizedUsername) &&
      String(user.pcc || '') === String(dashboardPcc || '')
    ))
    setDashboardSelectedUser(normalizedUsername)
    setEditingKey(configured ? `${configured.username}|${configured.parentPcc}|${configured.pcc}` : '')
    if (!selected && !configured) {
      setDashboardPermissionForm(emptyForm(parentPcc, dashboardPcc))
      return
    }
    const source = selected || configured
    setDashboardPermissionForm({
      username: String(source.username || source.id || ''),
      parentPcc: source.parentPcc || parentPcc || '',
      pcc: source.pcc || dashboardPcc || '',
      enableScriptGenerator: configured ? toBool(configured.enableScriptGenerator) : false,
      enableRepositories: configured ? toBool(configured.enableRepositories) : false,
    })
  }

  function editPermission(user) {
    const normalized = normalizePermission(user, parentPcc, dashboardPcc)
    const username = String(normalized.username || '')
    const pcc = normalized.pcc || dashboardPcc
    if (pcc !== dashboardPcc) {
      setDashboardPcc(pcc)
    }
    setDashboardSelectedUser(username)
    setDashboardPermissionForm({
      username,
      parentPcc: normalized.parentPcc || parentPcc || '',
      pcc,
      enableScriptGenerator: toBool(normalized.enableScriptGenerator),
      enableRepositories: toBool(normalized.enableRepositories),
    })
    setEditingKey(`${username}|${normalized.parentPcc || parentPcc || ''}|${pcc}`)
  }

  async function savePermission(event) {
    event.preventDefault()
    if (!dashboardPermissionForm.username) {
      toast('Selecciona un usuario', 'warning')
      return
    }
    const isEdit = Boolean(editingKey)
    const enableScriptGenerator = dashboardPermissionForm.enableScriptGenerator ? 1 : 0
    const enableRepositories = dashboardPermissionForm.enableRepositories ? 1 : 0
    const payload = {
      username: Number(dashboardPermissionForm.username),
      parentPcc: dashboardPermissionForm.parentPcc || parentPcc,
      parent_pcc: dashboardPermissionForm.parentPcc || parentPcc,
      pcc: dashboardPermissionForm.pcc || dashboardPcc,
      enableScriptGenerator,
      enable_script_generator: enableScriptGenerator,
      enableRepositories,
      enable_repositories: enableRepositories,
      updatedUser: 'admin_v2',
      updated_user: 'admin_v2',
    }
    console.log('[dashboard permissions] saving', payload)
    console.log('[dashboard permissions] mode', isEdit ? 'edit' : 'create')
    setSaving('save')
    const res = await request('dashboard_permissions_update', {
      method: 'POST',
      body: payload,
    })
    setSaving('')
    if (!res.ok) {
      const detail = res.data?.message || res.data?.error || 'No se pudo guardar el permiso'
      toast(detail, 'danger')
      return
    }
    toast('Permisos dashboard actualizados', 'success')
    await loadDashboardPermissions()
    setDashboardSelectedUser(String(payload.username))
    setDashboardPermissionForm({
      username: String(payload.username),
      parentPcc: payload.parentPcc,
      pcc: payload.pcc,
      enableScriptGenerator: payload.enableScriptGenerator === 1,
      enableRepositories: payload.enableRepositories === 1,
    })
    setEditingKey(`${payload.username}|${payload.parentPcc}|${payload.pcc}`)
  }

  async function deletePermission(user) {
    const username = Number(user.username || user.id)
    const confirmed = await confirm({
      title: 'Eliminar permisos',
      body: `Vas a eliminar los permisos dashboard del usuario ${username}.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      tone: 'danger',
      icon: 'cilTrash',
    })
    if (!confirmed) return
    const payload = {
      username,
      parentPcc: user.parentPcc || parentPcc,
      pcc: user.pcc || dashboardPcc,
    }
    setSaving(`delete-${username}-${payload.pcc}`)
    const res = await request('dashboard_permissions_delete', {
      method: 'POST',
      body: payload,
    })
    setSaving('')
    if (!res.ok) {
      const detail = res.data?.message || res.data?.error || 'No se pudo eliminar el permiso'
      toast(detail, 'danger')
      return
    }
    toast('Permisos dashboard eliminados', 'success')
    await loadDashboardPermissions()
  }

  function changeDashboardPcc(value) {
    const nextPcc = String(value || '').toUpperCase()
    setDashboardPcc(nextPcc)
    setDashboardSelectedUser('')
    setDashboardPermissionForm(emptyForm(parentPcc, nextPcc))
    setEditingKey('')
  }

  return (
    <section className="agency-mini-card">
      <h4><CIcon icon={icon('cilShieldAlt')} /> Permisos dashboard</h4>
      <p className="agency-hint">
        Controla solamente las tarjetas Generador de scripts y Repositorios para el Parent PCC {parentPcc}.
      </p>
      <CForm className="agency-inline-form two-cols" onSubmit={savePermission}>
        <div>
          <CFormLabel>PCC</CFormLabel>
          <CFormSelect value={dashboardPcc} disabled={pccs.length === 0} onChange={(event) => changeDashboardPcc(event.target.value)}>
            <option value="">{pccs.length ? 'Selecciona un PCC' : 'Sin PCCs disponibles'}</option>
            {pccs.map((pcc) => <option key={pcc.pcc} value={pcc.pcc}>{pcc.pcc}</option>)}
          </CFormSelect>
        </div>
        <div>
          <CFormLabel>Usuario</CFormLabel>
          <CFormSelect
            value={String(dashboardSelectedUser || '')}
            disabled={availableUsers.length === 0}
            onChange={(event) => selectUser(event.target.value)}
          >
            <option value="">{availableUsers.length ? 'Selecciona un usuario' : 'No hay usuarios disponibles'}</option>
            {dashboardSelectedUser && !availableUsers.some((user) => sameUsername(user.id, dashboardSelectedUser)) ? (
              <option value={String(dashboardSelectedUser)}>{dashboardSelectedUser}</option>
            ) : null}
            {availableUsers.map((user) => (
              <option key={`${user.id}-${user.parentPcc}-${user.pcc}`} value={String(user.id)}>
                {user.id} - {user.name || 'Sin nombre'}{user.email ? ` - ${user.email}` : ''}
              </option>
            ))}
          </CFormSelect>
        </div>
        <div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`dashboard-permission-script-${dashboardPermissionForm.username || 'new'}`}
              checked={Boolean(dashboardPermissionForm.enableScriptGenerator)}
              disabled={!dashboardPermissionForm.username}
              onChange={(event) => setDashboardPermissionForm((current) => ({
                ...current,
                enableScriptGenerator: event.target.checked,
              }))}
            />
            <label className="form-check-label" htmlFor={`dashboard-permission-script-${dashboardPermissionForm.username || 'new'}`}>
              Generador de scripts
            </label>
          </div>
        </div>
        <div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`dashboard-permission-repositories-${dashboardPermissionForm.username || 'new'}`}
              checked={Boolean(dashboardPermissionForm.enableRepositories)}
              disabled={!dashboardPermissionForm.username}
              onChange={(event) => setDashboardPermissionForm((current) => ({
                ...current,
                enableRepositories: event.target.checked,
              }))}
            />
            <label className="form-check-label" htmlFor={`dashboard-permission-repositories-${dashboardPermissionForm.username || 'new'}`}>
              Repositorios
            </label>
          </div>
        </div>
        <CButton color="success" className="btn-brand" type="submit" disabled={!dashboardPermissionForm.username || saving === 'save'}>
          <CIcon icon={icon('cilSave')} /> {saving === 'save' ? 'Guardando...' : 'Guardar'}
        </CButton>
        <CButton color="light" type="button" onClick={loadDashboardPermissions} disabled={loading || !dashboardPcc}>
          <CIcon icon={icon('cilReload')} /> {loading ? 'Cargando...' : 'Actualizar'}
        </CButton>
      </CForm>
      {availableUsers.length === 0 && !loading ? (
        <p className="agency-hint">No hay usuarios registrados para este Parent PCC/PCC.</p>
      ) : null}
      {loading ? <div className="agency-loading"><CSpinner color="success" /> Cargando permisos</div> : null}
      {!loading && dashboardPermissions.length === 0 ? (
        <p className="agency-hint">No hay permisos configurados para este PCC.</p>
      ) : null}
      {dashboardPermissions.length > 0 ? (
        <div className="table-responsive mb-3">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Parent PCC</th>
                <th>PCC</th>
                <th>Generador de scripts</th>
                <th>Repositorios</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {dashboardPermissions.map((user) => {
                const rowKey = `${user.username || user.id}-${user.parentPcc}-${user.pcc}`
                return (
                <tr key={rowKey} className={editingKey === `${user.username || user.id}|${user.parentPcc}|${user.pcc}` ? 'table-success' : ''}>
                  <td>
                    <strong>{user.username || user.id}</strong>
                    <div className="agency-mini-meta">
                      {user.name ? <span>{user.name}</span> : null}
                      {user.email ? <span>{user.email}</span> : null}
                    </div>
                  </td>
                  <td>{user.parentPcc || '-'}</td>
                  <td>{user.pcc || '-'}</td>
                  <td>{toBool(user.enableScriptGenerator) ? 'Activo' : 'Inactivo'}</td>
                  <td>{toBool(user.enableRepositories) ? 'Activo' : 'Inactivo'}</td>
                  <td>
                    <CButton color="light" size="sm" type="button" className="me-2" onClick={() => editPermission(user)}>
                      Editar
                    </CButton>
                    <CButton
                      color="danger"
                      size="sm"
                      variant="outline"
                      disabled={saving === `delete-${user.username || user.id}-${user.pcc}`}
                      onClick={() => deletePermission(user)}
                    >
                      Eliminar
                    </CButton>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}

export default DashboardPermissionsPanel
